import type { ReactElement } from 'react';
import {
  Archive,
  BadgeCheck,
  Banknote,
  BanknoteArrowDown,
  Bell,
  BellRing,
  BookOpenText,
  Boxes,
  Braces,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarCog,
  CalendarRange,
  CalendarX2,
  ChartColumn,
  ChartLine,
  ChartNetwork,
  CircleUserRound,
  ClipboardList,
  ClipboardPen,
  DatabaseZap,
  EyeOff,
  FileMinus,
  FilePen,
  FileUp,
  FileUser,
  Files,
  FolderKanban,
  GitBranch,
  HandCoins,
  Handshake,
  Hash,
  History,
  IdCard,
  Inbox,
  Laptop,
  LayoutDashboard,
  LayoutList,
  List,
  ListChecks,
  ListTodo,
  ListTree,
  MapPin,
  Megaphone,
  MessageSquareText,
  Network,
  PackageCheck,
  PartyPopper,
  Percent,
  Receipt,
  ReceiptText,
  Route,
  ScrollText,
  Shapes,
  ShieldAlert,
  ShieldUser,
  ShoppingBag,
  SlidersHorizontal,
  Smile,
  Split,
  SquareKanban,
  Tags,
  Target,
  TreePalm,
  Truck,
  UserSearch,
  UsersRound,
  Vault,
  Newspaper,
  Workflow,
} from 'lucide-react';
import { ChatNavIcon } from '../features/chat/ChatPage';
import type { MeResponse } from '@taqon/contracts';
import { hasCapability } from '../shared/session/session';
import type { ModuleKey } from '../shared/ui/modules';
import { DASHBOARD_LINKS } from '../features/reporting/dashboards';

/**
 * The navigation, grouped by module. An entry is shown when the session holds **any** of its
 * capabilities; a group is shown when any of its entries is. This is usability only: every page's
 * API enforces its own permission (SDD §22.4), and nothing here looks at a role name.
 *
 * Only pages that exist are listed — no module appears before it is delivered.
 */
export interface NavItem {
  readonly label: string;
  readonly to: string;
  readonly icon: ReactElement;
  /** Null: every signed-in user. Otherwise shown when any of these is held. */
  readonly anyOf: readonly string[] | null;
  /**
   * A management list: hidden when the capability is held only through own-records-only grants,
   * whose holders use the matching "my …" page instead (FRD-USR-023). The API decides either way.
   */
  readonly management?: true;
  /** Record pages that belong under this entry although their path differs (breadcrumb only). */
  readonly recordPrefixes?: readonly string[];
}

export interface NavGroup {
  readonly label: string | null;
  /** The module whose colour the group's entries and pages take. */
  readonly module: ModuleKey;
  readonly items: readonly NavItem[];
}

export const NAVIGATION: readonly NavGroup[] = [
  {
    label: null,
    module: 'home',
    items: [
      { label: 'Dashboard', to: '/', icon: <LayoutDashboard />, anyOf: null },
      {
        label: 'My approvals',
        to: '/approvals',
        icon: <BadgeCheck />,
        anyOf: ['platform.approval_step:view'],
      },
      {
        // Project work management (CLR-66): the caller's assigned tasks and their projects.
        label: 'My tasks',
        to: '/my-tasks',
        icon: <ListChecks />,
        anyOf: ['projects.task:view'],
      },
      {
        label: 'Project work',
        to: '/work',
        icon: <SquareKanban />,
        anyOf: ['projects.task:view'],
      },
      {
        label: 'Notifications',
        to: '/notifications',
        icon: <Bell />,
        anyOf: ['platform.notification:view'],
      },
      {
        // Internal chat (FRD §4.9), with the caller's unread count on its icon.
        label: 'Chat',
        to: '/chat',
        icon: <ChatNavIcon />,
        anyOf: ['platform.conversation:view'],
      },
    ],
  },
  {
    // The Release 1 dashboards (FRD-RPT-002), each shown to a session that may open it.
    label: 'Dashboards',
    module: 'reporting',
    items: DASHBOARD_LINKS.map((dashboard) => ({
      label: dashboard.title,
      to: `/dashboards/${dashboard.code}`,
      icon: <ChartLine />,
      anyOf: [dashboard.capability],
    })),
  },
  {
    label: 'Organization',
    module: 'organization',
    items: [
      {
        label: 'Legal entity',
        to: '/organization',
        icon: <Building2 />,
        anyOf: ['organization.organization:view'],
      },
      {
        label: 'Organization chart',
        to: '/organization/chart',
        icon: <ChartNetwork />,
        anyOf: ['organization.department:view'],
      },
      {
        label: 'Departments',
        to: '/organization/departments',
        icon: <Network />,
        anyOf: ['organization.department:view'],
      },
      {
        label: 'Cost centres',
        to: '/organization/cost-centres',
        icon: <Target />,
        anyOf: ['organization.cost_center:view'],
      },
      {
        label: 'Locations',
        to: '/organization/locations',
        icon: <MapPin />,
        anyOf: ['organization.location:view'],
      },
      {
        label: 'Products & services',
        to: '/organization/products-services',
        icon: <Boxes />,
        anyOf: ['organization.product_service:view'],
      },
    ],
  },
  {
    label: 'HR',
    module: 'hr',
    items: [
      {
        label: 'Employees',
        management: true,
        to: '/hr/employees',
        icon: <IdCard />,
        anyOf: ['hr.employee:view'],
      },
      {
        label: 'My employee record',
        to: '/hr/my-record',
        icon: <CircleUserRound />,
        anyOf: ['hr.employee:view'],
      },
      {
        label: 'Positions',
        to: '/hr/positions',
        icon: <BriefcaseBusiness />,
        anyOf: ['hr.position:view'],
      },
      // "Leave requests" precedes "My leave" so a request's page (/hr/leave/requests/:id) sits
      // under the list in the breadcrumb.
      {
        label: 'Leave requests',
        management: true,
        to: '/hr/leave/requests',
        icon: <CalendarCheck />,
        anyOf: ['hr.leave_request:view'],
      },
      {
        label: 'My leave',
        to: '/hr/leave',
        icon: <TreePalm />,
        anyOf: ['hr.leave_request:view'],
      },
      {
        label: 'Leave types',
        to: '/hr/leave-types',
        icon: <CalendarCog />,
        anyOf: ['hr.leave_type:view'],
      },
      {
        label: 'Public holidays',
        to: '/hr/public-holidays',
        icon: <PartyPopper />,
        anyOf: ['hr.public_holiday:view'],
      },
      // Company announcements (CLR-63): events and news shown on the dashboard.
      {
        label: 'Announcements',
        to: '/hr/announcements',
        icon: <Newspaper />,
        anyOf: ['hr.announcement:view'],
        recordPrefixes: ['/hr/announcements/'],
      },
      // Staff onboarding (CLR-48): HR's checklists and templates, and anyone's own tasks.
      {
        label: 'Onboarding',
        to: '/hr/onboarding',
        icon: <ClipboardList />,
        anyOf: ['hr.onboarding:view'],
      },
      {
        label: 'Onboarding templates',
        to: '/hr/onboarding-templates',
        icon: <LayoutList />,
        anyOf: ['hr.onboarding_template:view'],
      },
      {
        label: 'My onboarding tasks',
        to: '/hr/my-onboarding-tasks',
        icon: <ListTodo />,
        anyOf: ['hr.employee:view'],
      },
      {
        label: 'Employee requests',
        management: true,
        to: '/hr/requests',
        icon: <Inbox />,
        anyOf: ['hr.employee_request:view'],
      },
      {
        label: 'My requests',
        to: '/hr/my-requests',
        recordPrefixes: ['/hr/requests'],
        icon: <FileUser />,
        anyOf: ['hr.employee_request:view'],
      },
      {
        label: 'Happiness meter',
        to: '/hr/happiness',
        icon: <Smile />,
        anyOf: [
          'happiness.happiness_rating:create',
          'happiness.happiness_rating:view',
          'happiness.action_feedback:view',
        ],
      },
    ],
  },
  {
    label: 'Clients',
    module: 'crm',
    items: [
      {
        label: 'Clients',
        to: '/crm/clients',
        icon: <Handshake />,
        anyOf: ['crm.client:view'],
      },
      {
        label: 'Activities',
        to: '/crm/activities',
        icon: <CalendarClock />,
        anyOf: ['crm.client_activity:view'],
      },
      {
        label: 'Projects',
        to: '/projects',
        icon: <FolderKanban />,
        anyOf: ['projects.project:view'],
      },
    ],
  },
  {
    label: 'Marketing',
    module: 'marketing',
    items: [
      {
        label: 'Leads',
        to: '/marketing/leads',
        icon: <UserSearch />,
        anyOf: ['marketing.lead:view'],
      },
      {
        label: 'Campaigns',
        to: '/marketing/campaigns',
        icon: <Megaphone />,
        anyOf: ['marketing.campaign:view'],
      },
    ],
  },
  {
    label: 'Quality',
    module: 'quality',
    items: [
      {
        label: 'Quality issues',
        to: '/quality/issues',
        icon: <ShieldAlert />,
        anyOf: ['quality.quality_issue:view'],
      },
    ],
  },
  {
    label: 'Finance',
    module: 'finance',
    items: [
      {
        label: 'Chart of accounts',
        to: '/accounts',
        icon: <ListTree />,
        anyOf: ['ledger.account:view'],
      },
      {
        label: 'COA import',
        to: '/coa-import',
        icon: <FileUp />,
        anyOf: ['ledger.account:create'],
      },
      {
        label: 'Accounting periods',
        to: '/periods',
        icon: <CalendarRange />,
        anyOf: ['ledger.accounting_period:view'],
      },
      {
        label: 'Journals',
        to: '/journals',
        icon: <BookOpenText />,
        anyOf: ['ledger.journal:view'],
      },
      {
        label: 'Financial reports',
        to: '/reports',
        icon: <ChartColumn />,
        anyOf: [
          'ledger.journal:view',
          'finance.customer_invoice:view',
          'finance.supplier_invoice:view',
          'tax.vat_transaction_detail:view',
        ],
      },
      {
        label: 'Posting mappings',
        to: '/posting-mappings',
        icon: <Route />,
        anyOf: ['ledger.posting_account_mapping:view'],
      },
      {
        label: 'Tax configuration',
        to: '/tax',
        icon: <Percent />,
        anyOf: [
          'tax.vat_registration_period:view',
          'tax.tax_code:view',
          'tax.tax_determination_rule:view',
          'tax.tax_account_mapping:view',
        ],
      },
      {
        label: 'Supplier invoices',
        to: '/supplier-invoices',
        icon: <ReceiptText />,
        anyOf: ['finance.supplier_invoice:view'],
      },
      {
        label: 'Supplier credit notes',
        to: '/supplier-credit-notes',
        icon: <FileMinus />,
        anyOf: ['finance.supplier_credit_note:view'],
      },
      {
        label: 'Expense claims',
        to: '/expense-claims',
        icon: <Receipt />,
        anyOf: ['finance.expense_claim:view', 'finance.expense_claim:create'],
      },
      {
        label: 'Expense categories',
        to: '/expense-categories',
        icon: <Tags />,
        anyOf: ['finance.expense_category:view', 'finance.expense_category:create'],
      },
      {
        label: 'Customer invoices',
        to: '/customer-invoices',
        icon: <ScrollText />,
        anyOf: ['finance.customer_invoice:view'],
      },
      {
        label: 'Customer credit notes',
        to: '/customer-credit-notes',
        icon: <BanknoteArrowDown />,
        anyOf: ['finance.customer_credit_note:view'],
      },
      {
        label: 'Customer receipts',
        to: '/customer-receipts',
        icon: <HandCoins />,
        anyOf: ['finance.customer_receipt:view'],
      },
      {
        label: 'Bank & cash',
        to: '/bank-cash',
        icon: <Vault />,
        anyOf: ['finance.bank_cash_account:view'],
      },
      {
        label: 'Payments',
        to: '/payments',
        icon: <Banknote />,
        anyOf: ['finance.payment:view'],
      },
    ],
  },
  {
    label: 'Procurement',
    module: 'procurement',
    items: [
      {
        label: 'Suppliers',
        to: '/suppliers',
        icon: <Truck />,
        anyOf: ['procurement.supplier:view'],
      },
      {
        label: 'Purchase requests',
        to: '/procurement/purchase-requests',
        icon: <ClipboardPen />,
        anyOf: ['procurement.purchase_request:view'],
      },
      {
        label: 'My purchase requests',
        to: '/procurement/my-purchase-requests',
        icon: <FilePen />,
        anyOf: ['procurement.purchase_request:view'],
      },
      {
        label: 'Purchase orders',
        to: '/procurement/purchase-orders',
        icon: <ShoppingBag />,
        anyOf: ['procurement.purchase_order:view'],
      },
      {
        label: 'Receipts',
        to: '/procurement/receipts',
        icon: <PackageCheck />,
        anyOf: ['procurement.goods_receipt:view'],
      },
    ],
  },
  {
    label: 'Documents',
    module: 'documents',
    items: [
      {
        label: 'Expiring documents',
        to: '/documents/expiring',
        icon: <CalendarX2 />,
        anyOf: ['platform.document:view'],
      },
    ],
  },
  {
    label: 'Assets',
    module: 'assets',
    items: [
      {
        label: 'Asset register',
        to: '/assets',
        icon: <Archive />,
        anyOf: ['assets.asset:view'],
      },
      {
        label: 'My assets',
        to: '/assets/my',
        icon: <Laptop />,
        anyOf: ['assets.asset:view'],
      },
      {
        label: 'Asset classes',
        to: '/assets/classes',
        icon: <Shapes />,
        anyOf: ['assets.asset_class:view'],
      },
    ],
  },
  {
    label: 'Data migration',
    module: 'migration',
    items: [
      {
        label: 'Batches',
        to: '/migration/batches',
        icon: <DatabaseZap />,
        anyOf: ['migration.batch:view'],
      },
    ],
  },
  {
    label: 'Administration',
    module: 'admin',
    items: [
      {
        label: 'Users',
        to: '/admin/users',
        icon: <UsersRound />,
        anyOf: ['platform.app_user:view'],
      },
      {
        label: 'Roles & permissions',
        to: '/admin/roles',
        icon: <ShieldUser />,
        anyOf: ['platform.role:view', 'platform.role:create', 'platform.permission_grant:create'],
      },
      {
        label: 'Configuration',
        to: '/admin/configuration',
        icon: <SlidersHorizontal />,
        anyOf: ['platform.configuration_parameter:view'],
      },
      {
        label: 'List values',
        to: '/admin/lists',
        icon: <List />,
        anyOf: ['platform.configurable_list_value:view'],
      },
      {
        label: 'Number sequences',
        to: '/admin/number-sequences',
        icon: <Hash />,
        anyOf: ['platform.number_sequence:view'],
      },
      {
        label: 'Sensitive fields',
        to: '/admin/sensitive-fields',
        icon: <EyeOff />,
        anyOf: ['platform.sensitive_field_designation:view'],
      },
      {
        label: 'Segregation of duties',
        to: '/admin/sod-rules',
        icon: <Split />,
        anyOf: ['platform.sod_rule:view'],
      },
      {
        label: 'Approval routes',
        to: '/admin/workflows',
        icon: <GitBranch />,
        anyOf: ['platform.workflow_definition:view'],
      },
      {
        label: 'Approval chart',
        to: '/admin/approval-chart',
        icon: <Workflow />,
        anyOf: ['platform.workflow_definition:view'],
      },
      {
        label: 'Audit trail',
        to: '/admin/audit',
        icon: <History />,
        anyOf: ['platform.audit_record:view'],
      },
      {
        label: 'Notification rules',
        to: '/admin/notification-rules',
        icon: <BellRing />,
        anyOf: ['platform.notification_rule:view'],
      },
      {
        label: 'Group chats',
        to: '/admin/chat-groups',
        icon: <MessageSquareText />,
        anyOf: ['platform.chat_group:view'],
      },
      {
        label: 'Document types',
        to: '/admin/document-types',
        icon: <Files />,
        anyOf: ['platform.document_type:view'],
      },
      {
        label: 'API description',
        to: '/admin/api-description',
        icon: <Braces />,
        anyOf: ['platform.api_description:view'],
      },
    ],
  },
];

export function canSee(me: MeResponse, item: NavItem): boolean {
  const ownOnly = me.ownRecordsCapabilities ?? [];
  return (
    item.anyOf === null ||
    item.anyOf.some(
      (capability) =>
        hasCapability(me, capability) &&
        !(item.management === true && ownOnly.includes(capability)),
    )
  );
}

/** The groups and entries this session may see; empty groups are dropped. */
export function visibleNavigation(me: MeResponse): readonly NavGroup[] {
  return NAVIGATION.map((group) => ({
    label: group.label,
    module: group.module,
    items: group.items.filter((item) => canSee(me, item)),
  })).filter((group) => group.items.length > 0);
}

/**
 * The group and entry for a path, for the header breadcrumb. A record's own page sits under its
 * list (`/supplier-invoices/12` belongs to Supplier invoices).
 */
/**
 * The entries a sidebar search shows: those whose own label, or whose group's label, contains
 * every word typed, in any order and case. Groups left with no entry are dropped; a blank search
 * changes nothing.
 */
export function filterNavigation(groups: readonly NavGroup[], search: string): readonly NavGroup[] {
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return groups;
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        const text = `${group.label ?? ''} ${item.label}`.toLowerCase();
        return words.every((word) => text.includes(word));
      }),
    }))
    .filter((group) => group.items.length > 0);
}

export function locate(
  pathname: string,
  groups: readonly NavGroup[] = NAVIGATION,
): { group: string | null; module: ModuleKey; item: NavItem } | null {
  for (const group of groups) {
    for (const item of group.items) {
      if (item.to === pathname) return { group: group.label, module: group.module, item };
    }
  }
  // Otherwise the entry whose path (or declared record-page prefix) is the LONGEST prefix, so
  // `/organization/departments/7` sits under Departments, not the Legal entity page; the first entry
  // wins a tie, so a list listed before a "My …" page keeps its records.
  let best: { group: string | null; module: ModuleKey; item: NavItem; length: number } | null =
    null;
  for (const group of groups) {
    for (const item of group.items) {
      for (const prefix of [item.to, ...(item.recordPrefixes ?? [])]) {
        if (prefix === '/' || !pathname.startsWith(`${prefix}/`)) continue;
        if (best === null || prefix.length > best.length)
          best = { group: group.label, module: group.module, item, length: prefix.length };
      }
    }
  }
  return best === null ? null : { group: best.group, module: best.module, item: best.item };
}
